import { isModulrAccount } from '@/utils/accounts/is-modulr-account';
import { LegalEntityCountry, TPPAdapters } from '@seapoint/server/enums';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  TellMoneyConsent,
  TellMoneyNextGenPSD2AccountType,
  TellMoneyNextGenPSD2Permissions,
  TellMoneyOBIEPermission,
} from '../../../../server/src/adapters/tpp/tell-money/types/consent.types';
import { useTppApi } from '../../api/use-tpp-api';
import { useData } from '../../contexts/data-context/use-data';
import { useUserInfo } from '../../contexts/user-context/use-user-info';
import { Button } from '../../design-system/button/button';
import Loading from '../../design-system/loading/loading';
import { isBankingAccount } from '../../utils/accounts/is-banking-account';
import { getBankAccountName } from '../../utils/get-bank-account-name';

export const TPPConsentPage = ({ adapter }: { adapter: TPPAdapters }) => {
  const [searchParams] = useSearchParams();
  const consentId = searchParams.get('ConsentId');
  const { getConsent, submitConsentDecision, isLoading, error } = useTppApi({ adapter });
  const [consent, setConsent] = useState<TellMoneyConsent | null>(null);
  const { data, isLoading: isLoadingData } = useData();
  const { authenticatedInfo } = useUserInfo();

  // Store selected account IDs in a separate state
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const navigate = useNavigate();

  // Filter accounts based on consent gateway domain and account company legal entity
  const connectableAccounts = useMemo(() => {
    if (!data?.accounts || !consent) return [];

    const modulrAccounts = data.accounts.filter(isModulrAccount);

    // OBIE
    if (adapter === TPPAdapters.TELL_MONEY_UK) {
      return modulrAccounts.filter((account) => account.company.legalEntity.country === LegalEntityCountry.GB);
    }

    // NextGenPSD2
    const {
      accounts: accountPermissions,
      transactions: transactionPermissions,
      balances: balancePermissions,
    } = consent?.Permissions as TellMoneyNextGenPSD2Permissions;

    const permissions = [
      ...(accountPermissions || []),
      ...(transactionPermissions || []),
      ...(balancePermissions || []),
    ];

    return modulrAccounts.filter(isBankingAccount).filter((account) => {
      if (account.company.legalEntity.country === LegalEntityCountry.GB) {
        return false;
      }

      const permission = permissions.find((permission) => {
        if (permission.AccountType === TellMoneyNextGenPSD2AccountType.IBAN) {
          const ibanMatch = permission.AccountId === account.accountInformation.iban;

          if (!permission.Currency) {
            return ibanMatch;
          }

          const currencyMatch = permission.Currency === account.currency;

          return ibanMatch && currencyMatch;
        }

        return false;
      });

      return permission;
    });
  }, [data?.accounts, consent]);

  useEffect(() => {
    const fetchConsent = async () => {
      if (!consentId) return;

      const consentData = await getConsent(consentId);
      if (consentData) {
        setConsent(consentData);
      }
    };

    fetchConsent();
  }, [consentId]);

  // Auto-select all accounts for EU adapter
  useEffect(() => {
    if (adapter === TPPAdapters.TELL_MONEY_EU && connectableAccounts.length > 0) {
      const accountIds = connectableAccounts.map((account) => account.id);
      setSelectedAccountIds(accountIds);
    }
  }, [adapter, connectableAccounts]);

  const handleToggleAccount = (accountId: string) => {
    setSelectedAccountIds((prevSelectedIds) => {
      if (prevSelectedIds.includes(accountId)) {
        return prevSelectedIds.filter((id) => id !== accountId);
      } else {
        return [...prevSelectedIds, accountId];
      }
    });
  };

  const handleAccept = async () => {
    if (!consentId) return;

    await submitConsentDecision(consentId, true, selectedAccountIds);
  };

  const handleReject = async () => {
    if (!consentId) return;

    await submitConsentDecision(consentId, false);
  };

  if (isLoading || isLoadingData) {
    return <Loading text="Loading consent details..." />;
  }

  if (error) {
    return (
      <div className="flex h-screen flex-col items-center justify-center p-8 text-center">
        <h2 className="mb-4 text-2xl font-bold">Error</h2>
        <p className="mb-6 text-gray-600">{error}</p>
        <Button variant="default" onClick={() => navigate('/')}>
          Return to Dashboard
        </Button>
      </div>
    );
  }

  if (!consent) {
    return (
      <div className="flex h-screen flex-col items-center justify-center p-8 text-center">
        <h2 className="mb-4 text-2xl font-bold">Consent Not Found</h2>
        <p className="mb-6 text-gray-600">The requested consent could not be found or has expired.</p>
        <Button variant="default" onClick={() => navigate('/')}>
          Return to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col font-sans">
      <div className="flex flex-1 flex-col bg-gray-50 md:flex-row">
        <div className="flex-1 bg-white p-4 md:p-8">
          <h2 className="mb-2 text-xl font-semibold">Welcome {authenticatedInfo?.name}</h2>
          {adapter === TPPAdapters.TELL_MONEY_EU && (
            <p className="mb-6 text-gray-600">
              We have received a consent request. Make sure to control account and permission levels before giving the
              consent.
            </p>
          )}
          {adapter === TPPAdapters.TELL_MONEY_UK && (
            <p className="mb-6 text-gray-600">
              We have received a consent request. Please select the account or accounts you would like to grant access
              to or reject to refuse consent.
            </p>
          )}

          <div className="my-8 space-y-4">
            {connectableAccounts?.map((account) => {
              const accountName = getBankAccountName({
                displayName: account.displayName,
                institutionName: account.institution?.name,
              });

              return (
                <div key={account.id} className="flex items-center justify-between rounded-lg bg-gray-50 p-4 shadow-xs">
                  <div className="flex-1">
                    <h3 className="font-medium">
                      {account.company.legalEntity.name} - {accountName}
                    </h3>
                    <p className="text-sm text-gray-500">{account.currency}</p>

                    {adapter === TPPAdapters.TELL_MONEY_EU && consent?.Permissions && (
                      <div className="mt-2 text-xs text-gray-600">
                        <p className="font-medium">Permissions:</p>
                        <div className="mt-1 flex flex-wrap gap-2">
                          {(consent.Permissions as TellMoneyNextGenPSD2Permissions).accounts && (
                            <span className="rounded bg-blue-100 px-2 py-1">Accounts</span>
                          )}
                          {(consent.Permissions as TellMoneyNextGenPSD2Permissions).balances && (
                            <span className="rounded bg-green-100 px-2 py-1">Balances</span>
                          )}
                          {(consent.Permissions as TellMoneyNextGenPSD2Permissions).transactions && (
                            <span className="rounded bg-yellow-100 px-2 py-1">Transactions</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {adapter === TPPAdapters.TELL_MONEY_UK && (
                    <div
                      className={`relative flex h-9 w-20 cursor-pointer items-center rounded-full px-1 transition-colors ${
                        selectedAccountIds.includes(account.id)
                          ? 'justify-end bg-green-500'
                          : 'justify-start bg-red-500'
                      }`}
                      onClick={() => handleToggleAccount(account.id)}
                    >
                      <div className="h-7 w-7 rounded-full bg-white shadow-md"></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-8 flex gap-4">
            <Button
              variant="default"
              onClick={handleAccept}
              disabled={selectedAccountIds.length === 0}
              className="uppercase"
            >
              ACCEPT
            </Button>
            <Button variant="outline" onClick={handleReject} className="uppercase">
              REJECT
            </Button>
          </div>
        </div>

        <div className="flex-1 bg-yellow-400 p-4 text-gray-800 md:rounded-l-[50px] md:p-8">
          <h2 className="mb-4 text-xl font-semibold">Consent Details</h2>

          <div className="mt-6 space-y-3">
            <div className="flex">
              <span className="min-w-[150px] font-semibold">TPP:</span>
              <span>{consent.TppName}</span>
            </div>
            <div className="flex">
              <span className="min-w-[150px] font-semibold">TPP Website:</span>
              <span>{consent.TppWebsite}</span>
            </div>
            <div className="flex">
              <span className="min-w-[150px] font-semibold">TPP App:</span>
              <span>{consent.AppName}</span>
            </div>
            <div className="flex">
              <span className="min-w-[150px] font-semibold">App Description:</span>
              <span>{consent.AppDescription}</span>
            </div>
            <div className="flex">
              <span className="min-w-[150px] font-semibold">Consent Type:</span>
              <span>{consent.Scope}</span>
            </div>
            <div>
              {adapter === TPPAdapters.TELL_MONEY_UK && <span className="font-semibold">Permissions Requested:</span>}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {adapter === TPPAdapters.TELL_MONEY_UK &&
              (consent.Permissions as TellMoneyOBIEPermission[]).map((permission, index) => (
                <span key={index} className="rounded bg-white/30 px-2 py-1 text-sm">
                  {permission}
                </span>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};
